import { useState, useMemo, useRef } from 'react';
import Spinner from '../Spinner';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import { Workflow } from 'axiosApi/models';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import { handleAPIError } from 'common/errorHandler';
import ErrorSummary from 'components/common/ErrorSummary';
import { DataGrid, GridColDef, GridInitialState } from '@mui/x-data-grid';
import { DEFAULT_PAGINATION, useSearch } from 'hooks/useSearch';
import SearchInput from 'components/SearchInput';
import { Box, Button, IconButton, Tooltip, Typography, Card } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router';
import { hasFunctionality } from 'components/authorization/PermissionUtils';
import WorkflowActivateModal from './WorkflowActivateModal';

const Workflows = () => {
  const api: Api = useApi();
  const navigate = useNavigate();

  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [activateModal, setActivateModal] = useState<{ open: boolean; workflow: Workflow | null }>({
    open: false,
    workflow: null,
  });
  const importInputRef = useRef<HTMLInputElement>(null);

  const toast = useToastMessageQueue();

  const apiPromise = (
    searchTerm: string,
    pageNum: number,
    pageSize: number,
    sort: string,
    order: string
  ) => {
    if (searchTerm === "") {
      return api.workflowApi.apiVversionWorkflowAllGet("1", pageNum, pageSize, sort, order);
    } else {
      return api.workflowApi.apiVversionWorkflowSearchGet("1", searchTerm, pageNum, pageSize, sort, order);
    }
  };

  const processError = (error: any) => {
    handleAPIError(error, toast, errors);
    setErrors({ ...errors });
  };

  const handleExport = async (row: Workflow) => {
    try {
      const response = await api.workflowApi.apiVversionWorkflowIdGet(row.id, '1', {});
      const json = JSON.stringify(response.data?.data, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${row.name || 'workflow'}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
    }
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const parsed = JSON.parse(event.target?.result as string);

        // The exported JSON is WorkflowDetails — build a CreateCommand from it.
        // Use existing state IDs as reactFlowIds so transitions still link correctly.
        const states = (parsed.states ?? []).map((s: any) => ({
          id: 0,
          isDeleted: false,
          name: s.name,
          idWorkflow: 0,
          complete: s.complete,
          extraAttributes: s.extraAttributes,
          labelKey: s.labelKey,
          viewScopes: s.viewScopes,
          editScopes: s.editScopes,
          deleteScopes: s.deleteScopes,
          initial: s.initial,
          displayColor: s.displayColor,
          idStateType: s.idStateType ?? null,
          idWorkflowStateSuccess: null,
          idWorkflowStateFailure: null,
          parameterConfiguration: s.parameterConfiguration ?? null,
          reactFlowId: String(s.id),
        }));

        const transitions = (parsed.transitions ?? []).map((t: any) => ({
          id: 0,
          isDeleted: false,
          actionName: t.actionName,
          labelKey: t.labelKey,
          displayColor: t.displayColor,
          icon: t.icon,
          allowComments: t.allowComments,
          extraAttributes: t.extraAttributes,
          idScope: t.idScope,
          reactFlowId: `import-${t.id}`,
          stateFromReactFlowId: t.idWorkflowStateFrom ? String(t.idWorkflowStateFrom) : null,
          stateToReactFlowId: t.idWorkflowStateTo ? String(t.idWorkflowStateTo) : null,
        }));

        const createCommand = {
          name: parsed.name ?? parsed.workflow?.name ?? file.name.replace('.json', ''),
          idModule: parsed.idModule ?? parsed.workflow?.idModule ?? null,
          states,
          transitions,
          userEntityId: 0,
        };

        await api.workflowApi.apiVversionWorkflowPost('1', createCommand);
        toast.success({ message: intl.get('workflow.import.success') || 'Workflow imported successfully' });
        reFetchData();
      } catch (error) {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
      }
    };
    reader.readAsText(file);
  };

  const canEdit = hasFunctionality('ui.workflows.list.gate', 'edit');
  const canDelete = hasFunctionality('ui.workflows.list.gate', 'edit');

  const handleDelete = async (row: Workflow) => {
    if (!window.confirm(intl.get('workflow.delete.confirm') || `Delete workflow "${row.name}"?`)) return;
    try {
      await api.workflowApi.apiVversionWorkflowDelete('1', { id: row.id, userEntityId: 0 });
      toast.success({ message: intl.get('workflow.delete.success') || 'Workflow deleted' });
      reFetchData();
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
    }
  };

  const { data, loading, onPaginationModelChange, handleSortModelChange, paginationModel, reFetchData } = useSearch<Workflow>({
    apiPromise: apiPromise,
    searchTerm: searchTerm,
    defaultSort: { sort: 'Name', order: 'asc' },
    onError: processError,
    pageKey: 'workflows',
  });

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const INITIAL_STATE: GridInitialState = {
    pagination: {
      paginationModel: {
        page: DEFAULT_PAGINATION.pageNum,
        pageSize: DEFAULT_PAGINATION.pageSize,
      },
    },
    sorting: {
      sortModel: [
        {
          field: 'name',
          sort: 'asc',
        },
      ],
    },
  };

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'name',
      headerName: intl.get('workflowList.table.thead.name'),
      width: 250,
      flex: 1,
    },
    {
      field: 'module',
      headerName: intl.get('workflowList.table.thead.module'),
      width: 200,
      valueGetter: (value, row) => row.module?.name || '',
    },
    {
      field: 'active',
      headerName: intl.get('workflowList.table.thead.active'),
      width: 120,
      type: 'boolean',
    },
    {
      field: 'actions',
      headerName: '',
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <>
        <Box display="flex" alignItems="center">
          {canEdit && (
            <Tooltip title={intl.get('workflow.edit') || 'Edit'}>
              <IconButton size="small" onClick={() => navigate(`/workflow/${params.row.id}`)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={intl.get('workflow.export') || 'Export'}>
            <IconButton size="small" onClick={() => handleExport(params.row)}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canEdit && !params.row.active && (
            <Tooltip title={intl.get('workflow.activate') || 'Set Active'}>
              <IconButton
                size="small"
                onClick={() => setActivateModal({ open: true, workflow: params.row })}
              >
                <StarOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && !params.row.active && (
            <Tooltip title={intl.get('workflow.delete') || 'Delete'}>
              <IconButton size="small" color="error" onClick={() => handleDelete(params.row)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box></>
      ),
    },
  ], [navigate, canEdit, canDelete, data]);

  return (
    <Card sx={{ m: 2 }}>
    <Box sx={{ p: 3 }}>
      <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
        {intl.get('workflows.header')}
      </Typography>
      <Box>
        <ErrorSummary errors={errors} showEverything={true} />
        <PermissionsGate
          identifier="ui.workflows.list.gate"
          viewRoles={null}
          editRoles={null}
          RenderError={()=>{return <span>{intl.get('permissionsGate')}</span>}}
        >
          <Box sx={{ p: 3 }}>
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={2}>
              <SearchInput value={searchTerm} onChange={setSearchTerm} onClear={handleClearSearch} />
              <Box display="flex" gap={1}>
                {canEdit && (
                  <>
                    <input
                      ref={importInputRef}
                      type="file"
                      accept=".json"
                      style={{ display: 'none' }}
                      onChange={handleImport}
                    />
                    <Button onClick={() => importInputRef.current?.click()}>
                      <UploadIcon /> {intl.get('workflow.import') || 'Import'}
                    </Button>
                  </>
                )}
                <Button onClick={() => navigate(`/workflow/new`)}>
                  <AddIcon /> {intl.get('workflow.new') ? intl.get('workflow.new') : intl.get('button.new')}
                </Button>
              </Box>
            </Box>
            <Box style={{ width: '100%', display: 'flex' }}>
              {loading && <Spinner />}
              {data && (
                <DataGrid
                  rows={data.queryResult || []}
                  columns={columns}
                  rowCount={data.totalRecords || 0}
                  loading={loading}
                  paginationModel={paginationModel}
                  onPaginationModelChange={onPaginationModelChange}
                  onSortModelChange={handleSortModelChange}
                  autoHeight
                />
              )}
            </Box>
          </Box>
        </PermissionsGate>
      </Box>
    </Box>
    {activateModal.workflow && (
      <WorkflowActivateModal
        open={activateModal.open}
        onClose={() => setActivateModal({ open: false, workflow: null })}
        onSuccess={() => {
          setActivateModal({ open: false, workflow: null });
          reFetchData();
        }}
        workflowToActivate={activateModal.workflow}
        moduleWorkflows={(data?.queryResult || []).filter(
          (w) => w.idModule === activateModal.workflow?.idModule
        )}
      />
    )}
    </Card>
  );
};

export default Workflows;
